import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Plus,
  Edit2,
  Trash2,
  X,
  ExternalLink,
  Save,
  Filter,
  Upload,
  Image,
  Video,
  Link2,
  Play,
  Book,
  Mic,
  Share2,
  Send,
  RotateCcw,
  Shuffle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KnowledgeLibrary() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // State
  const [activeTab, setActiveTab] = useState('resources'); // 'resources', 'articles', 'glossary', or 'ai-assistant'
  const [uploadingImage, setUploadingImage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [expandedArticles, setExpandedArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [shuffling, setShuffling] = useState(false);

  // AI Assistant state
  const [aiMessage, setAiMessage] = useState('');
  const [aiChatHistory, setAiChatHistory] = useState([]);
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [includeHiveContext, setIncludeHiveContext] = useState(true);

  // Admin dialogs
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [showGlossaryDialog, setShowGlossaryDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [editingResource, setEditingResource] = useState(null);

  // Form states
  const [articleForm, setArticleForm] = useState({
    title: '', category: '', summary: '', content: '', image_url: '', references: [], order: 0
  });
  const [glossaryForm, setGlossaryForm] = useState({
    term: '', definition: '', related_terms: [], related_terms_input: ''
  });
  const [resourceForm, setResourceForm] = useState({
    title: '', description: '', url: '', resource_type: 'video', thumbnail_url: '', 
    author: '', platform: '', category: '', tags: [], tags_input: '', order: 0
  });
  const [fetchingMetadata, setFetchingMetadata] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    if (!token && localStorage.getItem('demoMode') === 'true') {
      setLoading(false);
      return;
    }
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch categories
      const catRes = await fetch(`${API_URL}/api/knowledge/categories`, { headers });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }

      // Fetch articles
      const artRes = await fetch(`${API_URL}/api/knowledge/articles`, { headers });
      if (artRes.ok) {
        const artData = await artRes.json();
        setArticles(artData);
      }

      // Fetch glossary
      const glosRes = await fetch(`${API_URL}/api/knowledge/glossary`, { headers });
      if (glosRes.ok) {
        const glosData = await glosRes.json();
        setGlossaryTerms(glosData);
      }

      // Fetch resource types
      const typeRes = await fetch(`${API_URL}/api/knowledge/resource-types`, { headers });
      if (typeRes.ok) {
        const typeData = await typeRes.json();
        setResourceTypes(typeData);
      }

      // Fetch resources
      const resRes = await fetch(`${API_URL}/api/knowledge/resources`, { headers });
      if (resRes.ok) {
        const resData = await resRes.json();
        setResources(resData);
      }
    } catch (error) {
    }
    setLoading(false);
  };

  // Shuffle all resources order (admin only)
  const handleShuffleResources = async () => {
    if (!token) return;
    setShuffling(true);
    try {
      const res = await fetch(`${API_URL}/api/knowledge/resources/shuffle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`🔀 ${data.message}`);
        // Reload resources to show new order
        const resRes = await fetch(`${API_URL}/api/knowledge/resources`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (resRes.ok) {
          const resData = await resRes.json();
          setResources(resData);
        }
      } else {
        toast.error('Failed to shuffle resources');
      }
    } catch (error) {
      toast.error('Failed to shuffle resources');
    }
    setShuffling(false);
  };

  // Seed initial data (admin only)
  const handleSeedData = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Seeded ${data.articles} articles and ${data.glossary_terms} glossary terms!`);
        fetchData();
      }
    } catch (error) {
    }
  };

  // Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/knowledge/search?q=${encodeURIComponent(searchQuery)}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (error) {
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  // AI Assistant handlers
  const handleAiChat = async () => {
    if (!aiMessage.trim() || !token) return;
    
    const userMsg = aiMessage.trim();
    setAiMessage('');
    setAiChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setAiLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/knowledge/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMsg,
          session_id: aiSessionId,
          include_hive_context: includeHiveContext
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiSessionId(data.session_id);
        setAiChatHistory(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to get AI response');
        setAiChatHistory(prev => prev.slice(0, -1)); // Remove the user message
      }
    } catch (error) {
      toast.error('Failed to connect to AI assistant');
      setAiChatHistory(prev => prev.slice(0, -1));
    }
    setAiLoading(false);
  };

  const handleNewAiSession = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge/ai-assistant/new-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiSessionId(data.session_id);
        setAiChatHistory([]);
        toast.success('Started new conversation');
      }
    } catch (error) {
    }
  };

  const handleClearAiHistory = async () => {
    if (!token) return;
    if (!window.confirm('Clear all chat history? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/knowledge/ai-assistant/history`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setAiChatHistory([]);
        setAiSessionId(null);
        toast.success('Chat history cleared');
      }
    } catch (error) {
    }
  };

  // Filter articles
  const filteredArticles = selectedCategory
    ? articles.filter(a => a.category === selectedCategory)
    : articles;

  // Filter glossary
  const filteredGlossary = selectedLetter
    ? glossaryTerms.filter(t => t.term.toUpperCase().startsWith(selectedLetter))
    : glossaryTerms;

  // Filter resources
  const filteredResources = selectedResourceType
    ? resources.filter(r => r.resource_type === selectedResourceType)
    : resources;

  // Group resources by type for display
  const groupedResources = {
    video: filteredResources.filter(r => r.resource_type === 'video'),
    social: filteredResources.filter(r => r.resource_type === 'social'),
    book: filteredResources.filter(r => r.resource_type === 'book'),
    article: filteredResources.filter(r => r.resource_type === 'article'),
    podcast: filteredResources.filter(r => r.resource_type === 'podcast'),
    other: filteredResources.filter(r => r.resource_type === 'other')
  };

  // Toggle article expansion
  const toggleArticle = (id) => {
    setExpandedArticles(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const imageUrl = `${API_URL}/api/uploads/${data.file_id}`;
        setArticleForm(prev => ({ ...prev, image_url: imageUrl }));
      }
    } catch (error) {
    } finally {
      setUploadingImage(false);
    }
  };

  // Article CRUD
  const handleSaveArticle = async () => {
    // Validate required fields
    if (!articleForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!articleForm.category) {
      toast.error('Category is required');
      return;
    }
    
    const method = editingArticle ? 'PUT' : 'POST';
    const url = editingArticle 
      ? `${API_URL}/api/knowledge/articles/${editingArticle.id}`
      : `${API_URL}/api/knowledge/articles`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(articleForm)
      });
      if (res.ok) {
        toast.success(editingArticle ? 'Article updated successfully!' : 'Article created successfully!');
        setShowArticleDialog(false);
        setEditingArticle(null);
        setArticleForm({ title: '', category: '', summary: '', content: '', image_url: '', references: [], order: 0 });
        fetchData();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to save article. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to save article. Please check your connection.');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge/articles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchData();
    } catch (error) {
    }
  };

  // Glossary CRUD
  const handleSaveGlossaryTerm = async () => {
    const method = editingTerm ? 'PUT' : 'POST';
    const url = editingTerm 
      ? `${API_URL}/api/knowledge/glossary/${editingTerm.id}`
      : `${API_URL}/api/knowledge/glossary`;
    
    // Convert the input string to array before saving
    const dataToSave = {
      term: glossaryForm.term,
      definition: glossaryForm.definition,
      related_terms: glossaryForm.related_terms_input
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
    };
    
    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });
      if (res.ok) {
        setShowGlossaryDialog(false);
        setEditingTerm(null);
        setGlossaryForm({ term: '', definition: '', related_terms: [], related_terms_input: '' });
        fetchData();
      }
    } catch (error) {
    }
  };

  const handleDeleteGlossaryTerm = async (id) => {
    if (!window.confirm('Are you sure you want to delete this term?')) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge/glossary/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchData();
    } catch (error) {
    }
  };

  // Open edit dialogs
  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      category: article.category,
      summary: article.summary,
      content: article.content,
      image_url: article.image_url || '',
      references: article.references || [],
      order: article.order || 0
    });
    setShowArticleDialog(true);
  };

  const openEditTerm = (term) => {
    setEditingTerm(term);
    setGlossaryForm({
      term: term.term,
      definition: term.definition,
      related_terms: term.related_terms || [],
      related_terms_input: (term.related_terms || []).join(', ')
    });
    setShowGlossaryDialog(true);
  };

  // Resource CRUD functions
  const handleSaveResource = async () => {
    if (!resourceForm.title || !resourceForm.url) {
      alert('Title and URL are required');
      return;
    }

    try {
      const tags = resourceForm.tags_input
        ? resourceForm.tags_input.split(',').map(t => t.trim()).filter(t => t)
        : resourceForm.tags || [];

      const payload = {
        ...resourceForm,
        tags
      };
      delete payload.tags_input;

      const url = editingResource
        ? `${API_URL}/api/knowledge/resources/${editingResource.id}`
        : `${API_URL}/api/knowledge/resources`;
      
      const res = await fetch(url, {
        method: editingResource ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowResourceDialog(false);
        setEditingResource(null);
        setResourceForm({
          title: '', description: '', url: '', resource_type: 'video', thumbnail_url: '',
          author: '', platform: '', category: '', tags: [], tags_input: '', order: 0
        });
        fetchData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to save resource');
      }
    } catch (error) {
      alert('Failed to save resource');
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/knowledge/resources/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
    }
  };

  const openEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title || '',
      description: resource.description || '',
      url: resource.url || '',
      resource_type: resource.resource_type || 'video',
      thumbnail_url: resource.thumbnail_url || '',
      author: resource.author || '',
      platform: resource.platform || '',
      category: resource.category || '',
      tags: resource.tags || [],
      tags_input: (resource.tags || []).join(', '),
      order: resource.order || 0
    });
    setShowResourceDialog(true);
  };

  const openAddResource = (type = 'video') => {
    setEditingResource(null);
    setResourceForm({
      title: '', description: '', url: '', resource_type: type, thumbnail_url: '',
      author: '', platform: '', category: '', tags: [], tags_input: '', order: 0
    });
    setShowResourceDialog(true);
  };

  // Fetch metadata from URL
  const handleFetchMetadata = async () => {
    if (!resourceForm.url) {
      alert('Please enter a URL first');
      return;
    }
    
    setFetchingMetadata(true);
    try {
      const res = await fetch(`${API_URL}/api/knowledge/resources/fetch-metadata`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: resourceForm.url })
      });
      
      if (res.ok) {
        const metadata = await res.json();
        setResourceForm(prev => ({
          ...prev,
          title: metadata.title || prev.title,
          description: metadata.description || prev.description,
          thumbnail_url: metadata.thumbnail_url || prev.thumbnail_url,
          platform: metadata.platform || prev.platform
        }));
      } else {
        alert('Could not fetch metadata from this URL');
      }
    } catch (error) {
      alert('Failed to fetch metadata');
    }
    setFetchingMetadata(false);
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return '🎬';
      case 'facebook': return '📘';
      case 'instagram': return '📷';
      case 'amazon': return '📦';
      case 'spotify': return '🎵';
      case 'tiktok': return '🎵';
      default: return '🔗';
    }
  };

  // Get resource type icon
  const getResourceTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5" />;
      case 'social': return <Share2 className="h-5 w-5" />;
      case 'book': return <Book className="h-5 w-5" />;
      case 'article': return <BookOpen className="h-5 w-5" />;
      case 'podcast': return <Mic className="h-5 w-5" />;
      default: return <Link2 className="h-5 w-5" />;
    }
  };

  // Get category info
  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { name: categoryId, icon: '📚' };
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="honeycomb-bg pb-8">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-amber-600" />
                Knowledge Library
              </h1>
              <p className="text-sm text-stone-500 mt-1">Learn everything about beekeeping</p>
            </div>
            {isAdmin && articles.length === 0 && (
              <Button onClick={handleSeedData} className="bg-amber-600 text-white hover:bg-amber-700">
                Load Library Content
              </Button>
            )}
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search articles and glossary..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white border-0 text-stone-900"
              />
              {searchQuery && (
                <button onClick={clearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <X className="h-4 w-4 text-stone-400 hover:text-stone-600" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} className="bg-white text-amber-600 hover:bg-amber-50">
              Search
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl px-4 pt-6">
        {/* Search Results */}
        {searchResults && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-stone-700">
                Search Results ({searchResults.total} found)
              </h2>
              <Button variant="ghost" size="sm" onClick={clearSearch}>
                Clear Search
              </Button>
            </div>

            {searchResults.articles.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-stone-500 mb-2">Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {searchResults.articles.map(article => (
                    <Card key={article.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                      clearSearch();
                      setActiveTab('articles');
                      setSelectedCategory(article.category);
                      setExpandedArticles({ [article.id]: true });
                    }}>
                      <CardContent className="p-3 flex items-center gap-3">
                        {article.image_url && (
                          <img src={article.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 truncate">{article.title}</p>
                          <p className="text-xs text-stone-500">{getCategoryInfo(article.category).name}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchResults.glossary.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-stone-500 mb-2">Glossary Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {searchResults.glossary.map(term => (
                    <Card key={term.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                      clearSearch();
                      setActiveTab('glossary');
                      setSelectedLetter(term.term[0].toUpperCase());
                    }}>
                      <CardContent className="p-3">
                        <p className="font-medium text-stone-800">{term.term}</p>
                        <p className="text-sm text-stone-500 line-clamp-2">{term.definition}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchResults.total === 0 && (
              <p className="text-stone-500 text-center py-8">No results found for "{searchQuery}"</p>
            )}
          </div>
        )}

        {/* Tabs */}
        {!searchResults && (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              <Button
                variant={activeTab === 'resources' ? 'default' : 'outline'}
                onClick={() => setActiveTab('resources')}
                className={activeTab === 'resources' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                <Link2 className="h-4 w-4 mr-2" />
                Resources ({resources.length})
              </Button>
              <Button
                variant={activeTab === 'articles' ? 'default' : 'outline'}
                onClick={() => setActiveTab('articles')}
                className={activeTab === 'articles' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Articles ({articles.length})
              </Button>
              <Button
                variant={activeTab === 'glossary' ? 'default' : 'outline'}
                onClick={() => setActiveTab('glossary')}
                className={activeTab === 'glossary' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                📖 Glossary ({glossaryTerms.length})
              </Button>
              <Button
                variant={activeTab === 'ai-assistant' ? 'default' : 'outline'}
                onClick={() => setActiveTab('ai-assistant')}
                className={activeTab === 'ai-assistant' ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
              >
                🤖 AI Assistant
              </Button>
            </div>

            {/* Articles Tab */}
            {activeTab === 'articles' && (
              <div>
                {/* Category Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={!selectedCategory ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory(null)}
                      className={`text-xs sm:text-sm ${!selectedCategory ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    >
                      All
                    </Button>
                    {categories.map(cat => (
                      <Button
                        key={cat.id}
                        variant={selectedCategory === cat.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`text-xs sm:text-sm ${selectedCategory === cat.id ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                      >
                        {cat.icon} <span className="hidden sm:inline ml-1">{cat.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Admin Add Button */}
                {isAdmin && (
                  <div className="mb-4">
                    <Button onClick={() => { setEditingArticle(null); setArticleForm({ title: '', category: '', summary: '', content: '', image_url: '', references: [], order: 0 }); setShowArticleDialog(true); }} className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Article
                    </Button>
                  </div>
                )}

                {/* Articles Grid */}
                <div className="space-y-4">
                  {filteredArticles.length === 0 ? (
                    <div className="text-center py-12 text-stone-500">
                      <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No articles found</p>
                      {isAdmin && <p className="text-sm mt-2">Click "Load Library Content" to add the starter content</p>}
                    </div>
                  ) : (
                    filteredArticles.map(article => (
                      <Card key={article.id} className="overflow-hidden">
                        <div 
                          className="flex items-start gap-4 p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                          onClick={() => toggleArticle(article.id)}
                        >
                          {/* Thumbnail */}
                          {article.image_url && (
                            <img 
                              src={article.image_url} 
                              alt={article.title}
                              className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0"
                            />
                          )}
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                                  {getCategoryInfo(article.category).icon} {getCategoryInfo(article.category).name}
                                </span>
                                <h3 className="font-semibold text-stone-800 mt-1">{article.title}</h3>
                                <p className="text-sm text-stone-500 line-clamp-2 mt-1">{article.summary}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {isAdmin && (
                                  <>
                                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditArticle(article); }} className="h-8 w-8 text-blue-600 hover:bg-blue-50">
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteArticle(article.id); }} className="h-8 w-8 text-red-600 hover:bg-red-50">
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {expandedArticles[article.id] ? (
                                  <ChevronUp className="h-5 w-5 text-stone-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-stone-400" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedArticles[article.id] && (
                          <div className="border-t bg-stone-50 p-4">
                            {article.image_url && (
                              <img 
                                src={article.image_url} 
                                alt={article.title}
                                className="w-full max-w-2xl rounded-lg mb-4 mx-auto"
                              />
                            )}
                            <div className="prose prose-stone max-w-none">
                              {article.content.split('\n').map((paragraph, i) => {
                                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                                  return <h4 key={i} className="font-bold text-stone-800 mt-4 mb-2">{paragraph.replace(/\*\*/g, '')}</h4>;
                                }
                                if (paragraph.startsWith('- ') || paragraph.startsWith('✓ ') || paragraph.startsWith('□ ')) {
                                  return <p key={i} className="text-stone-600 ml-4">{paragraph}</p>;
                                }
                                if (paragraph.trim()) {
                                  return <p key={i} className="text-stone-600 mb-2">{paragraph}</p>;
                                }
                                return null;
                              })}
                            </div>

                            {/* References */}
                            {article.references && article.references.length > 0 && (
                              <div className="mt-6 pt-4 border-t">
                                <h4 className="font-semibold text-stone-700 mb-2">References</h4>
                                <ul className="space-y-1">
                                  {article.references.map((ref, i) => (
                                    <li key={i}>
                                      <a 
                                        href={ref.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-amber-600 hover:text-amber-700 text-sm flex items-center gap-1"
                                      >
                                        {ref.title} <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Glossary Tab */}
            {activeTab === 'glossary' && (
              <div>
                {/* Alphabet Filter */}
                <div className="mb-6 overflow-x-auto">
                  <div className="flex gap-1 pb-2">
                    <Button
                      variant={!selectedLetter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedLetter(null)}
                      className={`w-10 ${!selectedLetter ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    >
                      All
                    </Button>
                    {alphabet.map(letter => {
                      const hasTerms = glossaryTerms.some(t => t.term.toUpperCase().startsWith(letter));
                      return (
                        <Button
                          key={letter}
                          variant={selectedLetter === letter ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedLetter(letter)}
                          disabled={!hasTerms}
                          className={`w-8 p-0 ${selectedLetter === letter ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                        >
                          {letter}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Admin Add Button */}
                {isAdmin && (
                  <div className="mb-4">
                    <Button onClick={() => { setEditingTerm(null); setGlossaryForm({ term: '', definition: '', related_terms: [], related_terms_input: '' }); setShowGlossaryDialog(true); }} className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Term
                    </Button>
                  </div>
                )}

                {/* Glossary List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredGlossary.length === 0 ? (
                    <div className="col-span-2 text-center py-12 text-stone-500">
                      <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No glossary terms found</p>
                    </div>
                  ) : (
                    filteredGlossary.map(term => (
                      <Card key={term.id} id={`glossary-term-${term.id}`} className="transition-all duration-300">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-amber-700">{term.term}</h3>
                              <p className="text-stone-600 text-sm mt-1">{term.definition}</p>
                              {term.related_terms && term.related_terms.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {term.related_terms.map((rt, i) => {
                                    const relatedTerm = glossaryTerms.find(t => t.term.toLowerCase() === rt.toLowerCase());
                                    return (
                                      <span 
                                        key={i} 
                                        className={`text-xs px-2 py-0.5 rounded ${
                                          relatedTerm 
                                            ? 'bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200 transition-colors' 
                                            : 'bg-stone-100 text-stone-500'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (relatedTerm) {
                                            setSelectedLetter(relatedTerm.term[0].toUpperCase());
                                            // Scroll to the term after a short delay
                                            setTimeout(() => {
                                              const element = document.getElementById(`glossary-term-${relatedTerm.id}`);
                                              if (element) {
                                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                element.classList.add('ring-2', 'ring-amber-400');
                                                setTimeout(() => element.classList.remove('ring-2', 'ring-amber-400'), 2000);
                                              }
                                            }, 100);
                                          }
                                        }}
                                        title={relatedTerm ? `Go to "${relatedTerm.term}"` : `"${rt}" not in glossary`}
                                      >
                                        {rt} {relatedTerm && '→'}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="flex gap-1 ml-2">
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditTerm(term); }} className="h-8 w-8 text-blue-600 hover:bg-blue-50">
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteGlossaryTerm(term.id); }} className="h-8 w-8 text-red-600 hover:bg-red-50">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && (
              <div>
                {/* Resource Type Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={!selectedResourceType ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedResourceType(null)}
                      className={`text-xs sm:text-sm ${!selectedResourceType ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                    >
                      All
                    </Button>
                    {resourceTypes.map(type => (
                      <Button
                        key={type.id}
                        variant={selectedResourceType === type.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedResourceType(type.id)}
                        className={`text-xs sm:text-sm ${selectedResourceType === type.id ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                      >
                        {type.icon} <span className="hidden sm:inline ml-1">{type.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Admin Add Buttons */}
                {isAdmin && (
                  <div className="mb-4 flex flex-wrap gap-2 items-center">
                    <Button onClick={() => openAddResource('video')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm">
                      <Video className="h-4 w-4 mr-1 sm:mr-2" /> Video
                    </Button>
                    <Button onClick={() => openAddResource('social')} size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm">
                      <Share2 className="h-4 w-4 mr-1 sm:mr-2" /> Social
                    </Button>
                    <Button onClick={() => openAddResource('book')} size="sm" className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm">
                      <Book className="h-4 w-4 mr-1 sm:mr-2" /> Book
                    </Button>
                    <Button onClick={() => openAddResource('podcast')} size="sm" className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm">
                      <Mic className="h-4 w-4 mr-1 sm:mr-2" /> Podcast
                    </Button>
                    <Button onClick={() => openAddResource('other')} size="sm" variant="outline" className="text-xs sm:text-sm">
                      <Link2 className="h-4 w-4 mr-1 sm:mr-2" /> Other
                    </Button>
                    <div className="h-6 w-px bg-stone-300 mx-1" />
                    <Button 
                      onClick={handleShuffleResources} 
                      size="sm" 
                      variant="outline" 
                      className="text-xs sm:text-sm border-purple-300 text-purple-700 hover:bg-purple-50"
                      disabled={shuffling || resources.length === 0}
                    >
                      <Shuffle className={`h-4 w-4 mr-1 sm:mr-2 ${shuffling ? 'animate-spin' : ''}`} /> 
                      {shuffling ? 'Shuffling...' : 'Shuffle Order'}
                    </Button>
                  </div>
                )}

                {/* Resources Display - Sectioned by Type */}
                {filteredResources.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Link2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No resources found</p>
                    {isAdmin && <p className="text-sm mt-2">Click the buttons above to add resources</p>}
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Videos Section */}
                    {groupedResources.video.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
                          <Video className="h-5 w-5" /> 🎬 Videos ({groupedResources.video.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.video.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Social Media Section */}
                    {groupedResources.social.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
                          <Share2 className="h-5 w-5" /> 📱 Social Media ({groupedResources.social.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.social.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Books Section */}
                    {groupedResources.book.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-amber-700 mb-4 flex items-center gap-2">
                          <Book className="h-5 w-5" /> 📚 Books ({groupedResources.book.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.book.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Articles Section */}
                    {groupedResources.article.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
                          <BookOpen className="h-5 w-5" /> 📰 External Articles ({groupedResources.article.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.article.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Podcasts Section */}
                    {groupedResources.podcast.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
                          <Mic className="h-5 w-5" /> 🎙️ Podcasts ({groupedResources.podcast.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.podcast.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Other Resources Section */}
                    {groupedResources.other.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-stone-700 mb-4 flex items-center gap-2">
                          <Link2 className="h-5 w-5" /> 🔗 Other Resources ({groupedResources.other.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {groupedResources.other.map(resource => (
                            <ResourceCard 
                              key={resource.id} 
                              resource={resource} 
                              onEdit={openEditResource}
                              onDelete={handleDeleteResource}
                              isAdmin={isAdmin}
                              getPlatformIcon={getPlatformIcon}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* AI Assistant Tab */}
            {activeTab === 'ai-assistant' && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-200 p-4 sm:p-6">
                <div className="max-w-4xl mx-auto">
                  {/* Header */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
                      <span className="text-3xl">🤖</span>
                    </div>
                    <h2 className="text-2xl font-bold text-stone-800">AI Beekeeping Assistant</h2>
                    <p className="text-stone-600 mt-2">Ask questions about beekeeping, get seasonal advice, and receive personalized recommendations based on your hive inspections.</p>
                  </div>

                  {/* Context Toggle */}
                  <div className="bg-white rounded-xl border border-stone-200 p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🐝</span>
                        <div>
                          <h3 className="font-medium text-stone-800">Include My Hive Data</h3>
                          <p className="text-xs text-stone-500">Let the AI reference your hives and recent inspections for personalized advice</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIncludeHiveContext(!includeHiveContext)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeHiveContext ? 'bg-blue-600' : 'bg-stone-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeHiveContext ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Chat History */}
                  <div className="bg-white rounded-xl border border-stone-200 mb-4 min-h-[300px] max-h-[500px] overflow-y-auto p-4">
                    {aiChatHistory.length === 0 ? (
                      <div className="text-center py-12 text-stone-400">
                        <span className="text-4xl mb-4 block">💬</span>
                        <p className="font-medium">Start a conversation!</p>
                        <p className="text-sm mt-1">Ask me anything about beekeeping...</p>
                        <div className="mt-6 flex flex-wrap justify-center gap-2">
                          {[
                            "What should I do this month?",
                            "How do I check for varroa mites?",
                            "When is the best time to split a hive?",
                            "My bees seem aggressive, what could be wrong?",
                          ].map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => setAiMessage(suggestion)}
                              className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {aiChatHistory.map((msg, idx) => (
                          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                              msg.role === 'user' 
                                ? 'bg-blue-600 text-white rounded-br-md' 
                                : 'bg-stone-100 text-stone-800 rounded-bl-md'
                            }`}>
                              {msg.role === 'assistant' && (
                                <div className="flex items-center gap-2 mb-2 text-xs text-stone-500">
                                  <span>🤖</span> AI Assistant
                                </div>
                              )}
                              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                                {msg.content}
                              </div>
                            </div>
                          </div>
                        ))}
                        {aiLoading && (
                          <div className="flex justify-start">
                            <div className="bg-stone-100 text-stone-800 rounded-2xl rounded-bl-md px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className="flex gap-2">
                    <Input
                      value={aiMessage}
                      onChange={(e) => setAiMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && aiMessage.trim()) {
                          e.preventDefault();
                          handleAiChat();
                        }
                      }}
                      placeholder="Ask about hive management, seasonal tasks, pest control..."
                      className="flex-1"
                      disabled={aiLoading}
                    />
                    <Button
                      onClick={handleAiChat}
                      disabled={!aiMessage.trim() || aiLoading}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      {aiLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </div>

                  {/* Actions */}
                  {aiChatHistory.length > 0 && (
                    <div className="flex justify-center gap-3 mt-4">
                      <button
                        onClick={handleNewAiSession}
                        className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
                      >
                        <RotateCcw className="h-4 w-4" />
                        New Conversation
                      </button>
                      <button
                        onClick={handleClearAiHistory}
                        className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Clear History
                      </button>
                    </div>
                  )}

                  {/* Tips */}
                  <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                      💡 Tips for best results
                    </h4>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Be specific about your situation (location, hive type, time of year)</li>
                      <li>• Enable "Include My Hive Data" for personalized recommendations</li>
                      <li>• Ask follow-up questions to dig deeper into topics</li>
                      <li>• The AI can help interpret your inspection findings</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Article Dialog */}
      {showArticleDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold">{editingArticle ? 'Edit Article' : 'Add Article'}</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowArticleDialog(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-sm font-medium text-stone-700">Title</label>
                <Input
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                  placeholder="Article title"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Category</label>
                <select
                  value={articleForm.category}
                  onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Summary</label>
                <Input
                  value={articleForm.summary}
                  onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
                  placeholder="Brief summary"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Image</label>
                <div className="space-y-2">
                  {/* Image Preview */}
                  {articleForm.image_url && (
                    <div className="relative inline-block">
                      <img 
                        src={articleForm.image_url} 
                        alt="Preview" 
                        className="h-32 w-auto rounded-lg object-cover border"
                      />
                      <Button 
                        variant="destructive" 
                        size="icon" 
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={() => setArticleForm({ ...articleForm, image_url: '' })}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="article-image-upload"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => document.getElementById('article-image-upload').click()}
                      disabled={uploadingImage}
                      className="flex-1"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </div>
                  
                  {/* Or enter URL */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-stone-500">Or enter URL:</span>
                    <Input
                      value={articleForm.image_url}
                      onChange={(e) => setArticleForm({ ...articleForm, image_url: e.target.value })}
                      placeholder="https://..."
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Content</label>
                <textarea
                  value={articleForm.content}
                  onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                  placeholder="Full article content..."
                  className="w-full border rounded-md p-2 h-48"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Order</label>
                <Input
                  type="number"
                  value={articleForm.order}
                  onChange={(e) => setArticleForm({ ...articleForm, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0 bg-white">
              <Button variant="outline" onClick={() => setShowArticleDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveArticle} className="bg-amber-600 hover:bg-amber-700">
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Glossary Dialog */}
      {showGlossaryDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingTerm ? 'Edit Term' : 'Add Term'}</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowGlossaryDialog(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Term</label>
                <Input
                  value={glossaryForm.term}
                  onChange={(e) => setGlossaryForm({ ...glossaryForm, term: e.target.value })}
                  placeholder="Term name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Definition</label>
                <textarea
                  value={glossaryForm.definition}
                  onChange={(e) => setGlossaryForm({ ...glossaryForm, definition: e.target.value })}
                  placeholder="Term definition..."
                  className="w-full border rounded-md p-2 h-32"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Related Terms (comma-separated)</label>
                <Input
                  value={glossaryForm.related_terms_input}
                  onChange={(e) => setGlossaryForm({ ...glossaryForm, related_terms_input: e.target.value })}
                  placeholder="Queen, Brood, Comb"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowGlossaryDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveGlossaryTerm} className="bg-amber-600 hover:bg-amber-700">
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resource Dialog */}
      {showResourceDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between bg-purple-50">
              <h2 className="text-lg font-semibold text-purple-800">
                {editingResource ? 'Edit Resource' : 'Add Resource'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setShowResourceDialog(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-stone-700">Title *</label>
                  <Input
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                    placeholder="Resource title"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-stone-700">URL *</label>
                  <div className="flex gap-2">
                    <Input
                      value={resourceForm.url}
                      onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=... or channel URL"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      onClick={handleFetchMetadata}
                      disabled={fetchingMetadata || !resourceForm.url}
                      className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
                    >
                      {fetchingMetadata ? '⏳ Fetching...' : '🔍 Auto-Fill'}
                    </Button>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    💡 Paste URL and click "Auto-Fill" to fetch title, thumbnail & description automatically
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Resource Type</label>
                  <select
                    value={resourceForm.resource_type}
                    onChange={(e) => setResourceForm({ ...resourceForm, resource_type: e.target.value })}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="video">🎬 Video</option>
                    <option value="social">📱 Social Media</option>
                    <option value="book">📚 Book</option>
                    <option value="article">📰 Article</option>
                    <option value="podcast">🎙️ Podcast</option>
                    <option value="other">🔗 Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Platform</label>
                  <select
                    value={resourceForm.platform}
                    onChange={(e) => setResourceForm({ ...resourceForm, platform: e.target.value })}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">Auto-detect</option>
                    <option value="youtube">YouTube</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="amazon">Amazon</option>
                    <option value="spotify">Spotify</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-stone-700">Description</label>
                  <textarea
                    value={resourceForm.description}
                    onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                    placeholder="Brief description of the resource..."
                    className="w-full border rounded-md p-2 h-24"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Author/Creator</label>
                  <Input
                    value={resourceForm.author}
                    onChange={(e) => setResourceForm({ ...resourceForm, author: e.target.value })}
                    placeholder="Channel name, author, etc."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Category</label>
                  <select
                    value={resourceForm.category}
                    onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-stone-700">Thumbnail URL (optional)</label>
                  <Input
                    value={resourceForm.thumbnail_url}
                    onChange={(e) => setResourceForm({ ...resourceForm, thumbnail_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                  {resourceForm.thumbnail_url && (
                    <img 
                      src={resourceForm.thumbnail_url} 
                      alt="Thumbnail preview" 
                      className="mt-2 h-24 w-auto rounded border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-stone-700">Tags (comma-separated)</label>
                  <Input
                    value={resourceForm.tags_input}
                    onChange={(e) => setResourceForm({ ...resourceForm, tags_input: e.target.value })}
                    placeholder="beginner, queen rearing, tutorial"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2 bg-stone-50">
              <Button variant="outline" onClick={() => setShowResourceDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveResource} className="bg-purple-600 hover:bg-purple-700">
                <Save className="h-4 w-4 mr-2" /> Save Resource
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Resource Card Component
function ResourceCard({ resource, onEdit, onDelete, isAdmin, getPlatformIcon }) {
  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'border-red-200 bg-red-50';
      case 'social': return 'border-blue-200 bg-blue-50';
      case 'book': return 'border-amber-200 bg-amber-50';
      case 'article': return 'border-green-200 bg-green-50';
      case 'podcast': return 'border-purple-200 bg-purple-50';
      default: return 'border-stone-200 bg-stone-50';
    }
  };

  return (
    <Card className={`overflow-hidden hover:shadow-md transition-shadow ${getTypeColor(resource.resource_type)}`}>
      {/* Thumbnail */}
      <a href={resource.url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="relative aspect-video bg-stone-200 overflow-hidden">
          {resource.thumbnail_url ? (
            <img 
              src={resource.thumbnail_url} 
              alt={resource.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform"
              onError={(e) => { 
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`absolute inset-0 flex items-center justify-center bg-stone-100 ${resource.thumbnail_url ? 'hidden' : ''}`}>
            <span className="text-3xl">
              {resource.resource_type === 'video' && '🎬'}
              {resource.resource_type === 'social' && '📱'}
              {resource.resource_type === 'book' && '📚'}
              {resource.resource_type === 'article' && '📰'}
              {resource.resource_type === 'podcast' && '🎙️'}
              {resource.resource_type === 'other' && '🔗'}
            </span>
          </div>
          {/* Play overlay for videos */}
          {resource.resource_type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
              <div className="bg-white/90 rounded-full p-2">
                <Play className="h-6 w-6 text-red-600 fill-current" />
              </div>
            </div>
          )}
          {/* Platform badge */}
          <div className="absolute top-1 right-1 bg-white/90 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
            {getPlatformIcon(resource.platform)} {resource.platform || 'Link'}
          </div>
        </div>
      </a>
      
      <CardContent className="p-2">
        <a href={resource.url} target="_blank" rel="noopener noreferrer" className="block group">
          <h4 className="font-semibold text-sm text-stone-800 line-clamp-2 group-hover:text-purple-700 transition-colors leading-tight">
            {resource.title}
          </h4>
        </a>
        {resource.author && (
          <p className="text-xs text-stone-500 mt-0.5 truncate">{resource.author}</p>
        )}
        {resource.description && (
          <p className="text-xs text-stone-600 mt-1 line-clamp-2">{resource.description}</p>
        )}
        {resource.tags && resource.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {resource.tags.slice(0, 2).map((tag, i) => (
              <span key={i} className="text-[10px] bg-white px-1.5 py-0.5 rounded border text-stone-600">
                {tag}
              </span>
            ))}
            {resource.tags.length > 2 && (
              <span className="text-[10px] text-stone-400">+{resource.tags.length - 2}</span>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t">
          <a 
            href={resource.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
          {isAdmin && (
            <div className="flex gap-0.5">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onEdit(resource)} 
                className="h-6 w-6 text-blue-600 hover:bg-blue-50"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onDelete(resource.id)} 
                className="h-6 w-6 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
